// Bar order board: live in-room QR orders, sound ping on new ones, one-tap prepared / delivered.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellOff, CheckCircle2, ChefHat, Loader2, XCircle } from 'lucide-react';
import { apiCall } from '../_shared';
import PushNotificationButton from '../PushNotificationButton';
import { OrderTiming } from './OrderTiming';
import { CashCount } from './CashCount';
import { money } from './BarTab';

const ping = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [0, 0.18].forEach((t) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.16);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.17);
    });
  } catch { /* no audio */ }
};

const age = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)); return m < 1 ? 'just now' : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`; };

const OrderCard = ({ o, busy, onStatus }) => {
  const lineList = o.items || [];
  const items = lineList.map((it) => <li key={it.sku} className="flex justify-between gap-2"><span>{it.qty}× {it.name}</span><span className="text-slate-500">{money(it.line_total)}</span></li>);
  const isNew = o.status === 'new';
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${isNew ? 'border-orange-400 bg-orange-50/60 shadow-sm' : 'bg-white'}`} data-testid={`order-card-${o.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold text-slate-900 text-base">{o.source === 'terrace' ? o.unit_name : o.unit_name ? `Room ${o.unit_name}` : o.room_name}{o.guest_name ? <span className="font-normal text-slate-600 text-sm"> · {o.guest_name}</span> : null}</p>
          <p className="text-[11px] text-slate-500">{age(o.created_at)} ago · {o.status}{o.bar_ticket_number ? ` · bar ticket #${o.bar_ticket_number}` : ''}{o.linked_folio_booking_id ? ` · charged to room${o.room_charge?.room_number ? ` ${o.room_charge.room_number}` : ''}` : o.room_charge && o.room_charge.matched === false ? ` · ⚠ room ${o.room_charge.room_number} not matched — collect payment` : o.source === 'terrace' ? ' · pay at the bar' : ' · not charged'}</p>
        </div>
        <span className="font-bold text-slate-900">{money(o.total_eur)}</span>
      </div>
      <ul className="text-sm text-slate-800">{items}</ul>
      {o.note && <p className="text-xs italic text-slate-600 bg-white/70 rounded px-2 py-1" data-testid={`order-note-${o.id}`}>“{o.note}”</p>}
      <div className="flex gap-2 pt-1">
        {isNew && <button type="button" disabled={busy} onClick={() => onStatus(o.id, 'prepared')} className="flex-1 inline-flex items-center justify-center gap-1 bg-slate-900 text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-50" data-testid={`order-prepared-${o.id}`}><ChefHat className="w-4 h-4" /> Prepared</button>}
        <button type="button" disabled={busy} onClick={() => onStatus(o.id, 'delivered')} className="flex-1 inline-flex items-center justify-center gap-1 bg-emerald-600 text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-50" data-testid={`order-delivered-${o.id}`}><CheckCircle2 className="w-4 h-4" /> Delivered</button>
        <button type="button" disabled={busy} onClick={() => { if (window.confirm('Cancel this order? Stock returns to the bar.')) onStatus(o.id, 'cancelled'); }} className="inline-flex items-center justify-center gap-1 border rounded-lg px-3 py-2 text-sm text-slate-600 hover:text-red-600 hover:border-red-300 disabled:opacity-50" data-testid={`order-cancel-${o.id}`}><XCircle className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const BarOrders = ({ onCountChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sound, setSound] = useState(() => localStorage.getItem('bar_orders_sound') !== 'off');
  const [err, setErr] = useState('');
  const seen = useRef(null);
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const [calls, setCalls] = useState([]);
  const seenCalls = useRef(new Set());
  const loadCalls = useCallback(async () => {
    try {
      const r = await apiCall('/api/admin/terrace/calls?status=open');
      const list = r.calls || [];
      if (list.some((c) => !seenCalls.current.has(c.id)) && seenCalls.current.size) if (soundRef.current) ping();
      list.forEach((c) => seenCalls.current.add(c.id));
      setCalls(list);
    } catch { /* keep last */ }
  }, []);
  const closeCall = async (id) => { try { await apiCall(`/api/admin/terrace/calls/${id}`, { method: 'PATCH' }); setCalls((p) => p.filter((c) => c.id !== id)); } catch { /* ignore */ } };
  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/admin/inroom/orders?limit=60');
      const cutoff = Date.now() - 48 * 3600 * 1000;
      const open = r.items.filter((o) => (o.status === 'new' || o.status === 'prepared') && new Date(o.created_at).getTime() > cutoff);
      const newIds = new Set(open.filter((o) => o.status === 'new').map((o) => o.id));
      if (seen.current && [...newIds].some((id) => !seen.current.has(id)) && soundRef.current) ping();
      seen.current = newIds;
      setData({ open, done: r.items.filter((o) => o.status === 'delivered').slice(0, 8), counts: r.counts });
      onCountChange?.(r.counts.new);
      setErr('');
    } catch (e) { setErr(e.message); }
  }, [onCountChange]);
  useEffect(() => { load(); loadCalls(); const t = setInterval(() => { load(); loadCalls(); }, 15000); return () => clearInterval(t); }, [load, loadCalls]);
  const setStatus = async (id, status) => {
    setBusy(id);
    try { await apiCall(`/api/admin/inroom/orders/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }); await load(); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const toggleSound = () => { const v = !sound; setSound(v); localStorage.setItem('bar_orders_sound', v ? 'on' : 'off'); if (v) ping(); };
  if (!data) return <p className="text-sm text-slate-500">{err || 'Loading orders…'}</p>;
  const callList = calls;
  const callCards = callList.map((c) => (
    <div key={c.id} className="rounded-xl border-2 border-orange-400 bg-orange-50 px-3 py-2 flex items-center gap-3" data-testid={`call-card-${c.id}`}>
      <Bell className="w-5 h-5 text-orange-600 animate-pulse" />
      <div className="flex-1 min-w-0"><p className="font-bold text-slate-900">{c.spot_label}</p><p className="text-[11px] text-slate-600">{c.type === 'bill' ? 'Bill requested' : 'Waiter requested'} · {age(c.created_at) === 'just now' ? 'just now' : `${age(c.created_at)} ago`}</p></div>
      <button type="button" onClick={() => closeCall(c.id)} className="bg-slate-900 text-white rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid={`call-done-${c.id}`}>Done</button>
    </div>
  ));
  const openList = data.open;
  const cards = openList.map((o) => <OrderCard key={o.id} o={o} busy={busy === o.id} onStatus={setStatus} />);
  const doneList = data.done;
  const summaryOf = (o) => { const ls = o.items || []; return ls.map((i) => `${i.qty}× ${i.name}`).join(', '); };
  const done = doneList.map((o) => <li key={o.id} className="text-[11px] text-slate-500 py-0.5 border-t" data-testid={`order-done-${o.id}`}>{String(o.handled_at || o.created_at).slice(11, 16)} · {o.source === 'terrace' ? o.unit_name : o.unit_name ? `Room ${o.unit_name}` : o.room_name} · {summaryOf(o)} · {money(o.total_eur)}</li>);
  return (
    <div className="space-y-3" data-testid="bar-orders">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-slate-800" data-testid="bar-orders-count">{data.counts.new} new · {data.counts.prepared} prepared</span>
        <span className="text-slate-400">· refreshes every 15 s</span>
        <span className="ml-auto"><PushNotificationButton role="bar" /></span>
        <button type="button" onClick={toggleSound} className={`inline-flex items-center gap-1 border rounded-full px-3 py-1 ${sound ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`} data-testid="bar-orders-sound">{sound ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />} Sound {sound ? 'on' : 'off'}</button>
      </div>
      {err && <p className="text-xs text-red-600" data-testid="bar-orders-error">{err}</p>}
      {callCards.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mb-3" data-testid="bar-calls">{callCards}</div>}
      {cards.length ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{cards}</div> : <p className="text-sm text-slate-400 bg-white border rounded-xl p-6 text-center" data-testid="bar-orders-empty">No open orders — new QR orders from the terrace appear here with a ping.</p>}
      {done.length > 0 && <details className="bg-white border rounded-xl p-3"><summary className="text-xs font-semibold text-slate-700 cursor-pointer">Recently delivered</summary><ul className="mt-1">{done}</ul></details>}
      <CashCount />
      <OrderTiming />
    </div>
  );
};

export default BarOrders;
